import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getCurrentLocation, buildLocationParams } from '../utils/location';
import { formatIndianDate } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Eye, MessageSquare, ListTodo, CheckCircle, XCircle, Clock, AlertTriangle, TrendingUp, Pause, ChevronsUpDown, Check, Phone, Building2, Calendar } from 'lucide-react';

const SalesMarketingDashboard = () => {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, success: 0, failed: 0, postponed: 0, overdue: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [createLeadDialogOpen, setCreateLeadDialogOpen] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');

  const [leadFormData, setLeadFormData] = useState({
    customer_type: 'Existing', customer_id: '', lead_source: '', inquiry_details: '', due_date: '', non_customer_name: '', non_customer_mobile: '', interested_products: []
  });

  const [followupFormData, setFollowupFormData] = useState({
    followup_date: new Date().toISOString().split('T')[0], notes: '', next_due_date: '', status_update: ''
  });

  // Check session validity on dashboard load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await api.get('/auth/check-session');
        if (!response.data.valid) {
          toast.error(response.data.reason || 'Session expired. Please login again.');
          logout();
        }
      } catch (error) {
        if (error.response?.status === 401) {
          logout();
        }
      }
    };
    
    if (user) {
      checkSession();
    }
  }, [user, logout]);

  useEffect(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(today.getDate() - 90);
    setFromDate(ninetyDaysAgo.toISOString().split('T')[0]);
    setToDate(today.toISOString().split('T')[0]);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get('/leads/', { params });
      setLeads(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, fromDate, toDate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/leads/sales/stats');
      setStats(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data.filter(p => p.status === 'Active'));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) fetchLeads();
    fetchStats();
    fetchCustomers();
    fetchProducts();
  }, [fetchLeads, fetchStats, fromDate, toDate]);

  const handleViewLead = async (lead) => {
    try {
      const response = await api.get(`/leads/${lead.id}`);
      setSelectedLead(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load lead');
    }
  };

  const handleOpenFollowupDialog = (lead) => {
    setSelectedLead(lead);
    setFollowupFormData({ followup_date: new Date().toISOString().split('T')[0], notes: '', next_due_date: '', status_update: '' });
    setFollowupDialogOpen(true);
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    if (!followupFormData.notes.trim()) { toast.error('Notes required'); return; }
    setLoading(true);
    try {
      let location;
      try { location = await getCurrentLocation(); toast.info('Location captured', { duration: 1500 }); }
      catch (locError) { toast.error(locError.message); setLoading(false); return; }
      const locationParams = buildLocationParams(location);
      await api.post(`/leads/${selectedLead.id}/followups?${locationParams}`, { lead_id: selectedLead.id, ...followupFormData });
      toast.success('Followup added');
      setFollowupDialogOpen(false);
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateLead = () => {
    setLeadFormData({ customer_type: 'Existing', customer_id: '', lead_source: '', inquiry_details: '', due_date: '', non_customer_name: '', non_customer_mobile: '', interested_products: [] });
    setCustomerSearchTerm('');
    setProductSearchTerm('');
    setCreateLeadDialogOpen(true);
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!leadFormData.lead_source || !leadFormData.inquiry_details || !leadFormData.due_date) { toast.error('Fill required fields'); return; }
    if (leadFormData.customer_type === 'Existing' && !leadFormData.customer_id) { toast.error('Select customer'); return; }
    if (leadFormData.customer_type === 'Non-Customer' && (!leadFormData.non_customer_name || !leadFormData.non_customer_mobile)) { toast.error('Provide contact details'); return; }
    setLoading(true);
    try {
      let location;
      try { location = await getCurrentLocation(); toast.info('Location captured', { duration: 1500 }); }
      catch (locError) { toast.error(locError.message); setLoading(false); return; }
      const locationParams = buildLocationParams(location);
      await api.post(`/leads/?${locationParams}`, { ...leadFormData, assigned_employee_id: user.id });
      toast.success('Lead created');
      setCreateLeadDialogOpen(false);
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const handleAddProduct = (product) => {
    if (!leadFormData.interested_products.find(p => p.id === product.id)) {
      setLeadFormData(prev => ({
        ...prev,
        interested_products: [...prev.interested_products, { id: product.id, name: product.name, sku: product.sku }]
      }));
    }
    setProductSearchOpen(false);
    setProductSearchTerm('');
  };

  const handleRemoveProduct = (productId) => {
    setLeadFormData(prev => ({
      ...prev,
      interested_products: prev.interested_products.filter(p => p.id !== productId)
    }));
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Success' || status === 'Failed') return false;
    return new Date(dueDate) < new Date();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'On Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Success': return 'bg-green-100 text-green-800';
      case 'Failed': return 'bg-red-100 text-red-800';
      case 'Postponed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.company_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.contact_person?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.mobile?.includes(customerSearchTerm)
  );

  const selectedCustomer = customers.find(c => c.id === leadFormData.customer_id);

  const filteredLeads = leads.filter(lead => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'overdue') return isOverdue(lead.due_date, lead.lead_status);
    return lead.lead_status === statusFilter;
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Sales Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">Welcome, {user?.name}!</p>
        </div>
        <Button onClick={handleOpenCreateLead} className="w-full sm:w-auto">
          <Plus size={16} className="mr-2" /> New Lead
        </Button>
      </div>

      {/* Stats Cards - Mobile Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Total</p>
                <p className="text-xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <ListTodo className="text-blue-600 h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('pending')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Pending</p>
                <p className="text-xl font-bold text-orange-600">{stats.pending}</p>
              </div>
              <Clock className="text-orange-600 h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer active:scale-95 transition-transform ${stats.overdue > 0 ? 'border-red-300 bg-red-50' : ''}`} onClick={() => setStatusFilter('overdue')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Overdue</p>
                <p className={`text-xl font-bold ${stats.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>{stats.overdue}</p>
              </div>
              <AlertTriangle className={`h-5 w-5 ${stats.overdue > 0 ? 'text-red-600 animate-pulse' : 'text-gray-400'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('Success')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Won</p>
                <p className="text-xl font-bold text-green-600">{stats.success}</p>
              </div>
              <CheckCircle className="text-green-600 h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('Failed')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Lost</p>
                <p className="text-xl font-bold text-red-500">{stats.failed}</p>
              </div>
              <XCircle className="text-red-500 h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('Postponed')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Postponed</p>
                <p className="text-xl font-bold text-purple-600">{stats.postponed}</p>
              </div>
              <Pause className="text-purple-600 h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads List */}
      <Card>
        <CardHeader className="p-3 sm:p-4 pb-2">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <div>
                <CardTitle className="text-base sm:text-lg">My Leads</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Manage your leads</CardDescription>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="On Progress">In Progress</SelectItem>
                  <SelectItem value="Success">Won</SelectItem>
                  <SelectItem value="Failed">Lost</SelectItem>
                  <SelectItem value="Postponed">Postponed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Date Range - Collapsible on Mobile */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-start sm:items-center">
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Label className="text-xs text-gray-600 whitespace-nowrap">From:</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="flex-1 sm:w-32 h-8 text-sm" />
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Label className="text-xs text-gray-600 whitespace-nowrap">To:</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="flex-1 sm:w-32 h-8 text-sm" />
              </div>
              <span className="text-xs text-gray-400 hidden sm:inline">(Last 90 days)</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          {loading && !leads.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp size={40} className="mx-auto mb-4 text-gray-300" />
              <p className="text-sm">No leads found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLeads.map((lead) => (
                <div
                  key={lead.id}
                  className={`p-3 rounded-lg border ${isOverdue(lead.due_date, lead.lead_status) ? 'bg-red-50 border-red-200' : 'bg-white'}`}
                >
                  {/* Lead Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{lead.lead_no}</span>
                        <Badge className={getStatusColor(lead.lead_status)}>{lead.lead_status}</Badge>
                        {lead.followup_count > 0 && (
                          <Badge variant="outline" className="text-xs">{lead.followup_count} followups</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate">{lead.customer?.company_name || lead.non_customer_name || 'N/A'}</span>
                    </div>
                    {(lead.customer?.mobile || lead.non_customer_mobile) && (
                      <a href={`tel:${lead.customer?.mobile || lead.non_customer_mobile}`} className="flex items-center gap-2 text-sm text-blue-600">
                        <Phone size={14} className="flex-shrink-0" />
                        {lead.customer?.mobile || lead.non_customer_mobile}
                      </a>
                    )}
                  </div>

                  {/* Source & Date */}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                    <Badge variant="outline">{lead.lead_source}</Badge>
                    <span className={`flex items-center gap-1 ${isOverdue(lead.due_date, lead.lead_status) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      <Calendar size={12} />
                      {isOverdue(lead.due_date, lead.lead_status) && <AlertTriangle size={12} />}
                      Due: {new Date(lead.due_date).toLocaleDateString('en-IN')}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleViewLead(lead)} className="flex-1 sm:flex-none">
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    {lead.lead_status !== 'Success' && lead.lead_status !== 'Failed' && (
                      <Button size="sm" variant="outline" onClick={() => handleOpenFollowupDialog(lead)} className="flex-1 sm:flex-none">
                        <MessageSquare size={14} className="mr-1" /> Followup
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Lead Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg">{selectedLead?.lead_no}</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-gray-500 text-xs">Customer</Label>
                  <p className="font-medium">{selectedLead.customer?.company_name || selectedLead.non_customer_name}</p>
                  <a href={`tel:${selectedLead.customer?.mobile || selectedLead.non_customer_mobile}`} className="text-blue-600 text-sm">
                    {selectedLead.customer?.mobile || selectedLead.non_customer_mobile}
                  </a>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Source</Label>
                  <p className="font-medium">{selectedLead.lead_source}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Due Date</Label>
                  <p className="font-medium">{new Date(selectedLead.due_date).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Status</Label>
                  <Badge className={getStatusColor(selectedLead.lead_status)}>{selectedLead.lead_status}</Badge>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-xs">Inquiry</Label>
                <p className="mt-1 p-2 bg-gray-50 rounded text-sm">{selectedLead.inquiry_details}</p>
              </div>

              {selectedLead.followups?.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs flex items-center gap-1">
                    <MessageSquare size={12} /> Followups ({selectedLead.followups.length})
                  </Label>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {selectedLead.followups.map((f, i) => (
                      <div key={f.id || i} className="p-2 bg-gray-50 rounded border-l-4 border-blue-400 text-sm">
                        <div className="flex justify-between items-start gap-2">
                          <p>{f.notes}</p>
                          {f.status_update && <Badge className={getStatusColor(f.status_update)} size="sm">→ {f.status_update}</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{f.creator?.name} - {formatIndianDate(f.created_at)}</p>
                        {f.next_due_date && <p className="text-xs text-orange-600 mt-1"><Clock size={10} className="inline mr-1" />Next: {new Date(f.next_due_date).toLocaleDateString('en-IN')}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="w-full sm:w-auto">Close</Button>
            {selectedLead?.lead_status !== 'Success' && selectedLead?.lead_status !== 'Failed' && (
              <Button onClick={() => { setViewDialogOpen(false); handleOpenFollowupDialog(selectedLead); }} className="w-full sm:w-auto">
                <MessageSquare size={14} className="mr-2" /> Followup
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Followup Dialog */}
      <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Add Followup</DialogTitle>
            <DialogDescription className="text-sm">{selectedLead?.lead_no}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddFollowup} className="space-y-4">
            <div>
              <Label className="text-sm">Date</Label>
              <Input type="date" value={followupFormData.followup_date} onChange={(e) => setFollowupFormData(p => ({ ...p, followup_date: e.target.value }))} required />
            </div>
            <div>
              <Label className="text-sm">Notes *</Label>
              <Textarea value={followupFormData.notes} onChange={(e) => setFollowupFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Notes..." rows={3} required />
            </div>
            <div>
              <Label className="text-sm">Update Status</Label>
              <Select value={followupFormData.status_update || 'no-change'} onValueChange={(v) => setFollowupFormData(p => ({ ...p, status_update: v === 'no-change' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Keep current" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-change">Keep current</SelectItem>
                  <SelectItem value="On Progress">In Progress</SelectItem>
                  <SelectItem value="Success">Won</SelectItem>
                  <SelectItem value="Failed">Lost</SelectItem>
                  <SelectItem value="Postponed">Postponed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Next Due Date</Label>
              <Input type="date" value={followupFormData.next_due_date} onChange={(e) => setFollowupFormData(p => ({ ...p, next_due_date: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setFollowupDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto">{loading ? 'Adding...' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Lead Dialog */}
      <Dialog open={createLeadDialogOpen} onOpenChange={setCreateLeadDialogOpen}>
        <DialogContent className="max-w-sm mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Lead</DialogTitle>
            <DialogDescription className="text-sm">Auto-assigned to you</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4">
            <div>
              <Label className="text-sm">Customer Type *</Label>
              <Select value={leadFormData.customer_type} onValueChange={(v) => setLeadFormData(p => ({ ...p, customer_type: v, customer_id: '', non_customer_name: '', non_customer_mobile: '' }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Existing">Existing Customer</SelectItem>
                  <SelectItem value="Non-Customer">New Contact</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {leadFormData.customer_type === 'Existing' ? (
              <div>
                <Label className="text-sm">Customer *</Label>
                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between text-left">
                      {selectedCustomer ? (
                        <span className="truncate">{selectedCustomer.company_name}</span>
                      ) : (
                        <span className="text-gray-500">Select...</span>
                      )}
                      <ChevronsUpDown className="h-4 w-4 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Search..." value={customerSearchTerm} onValueChange={setCustomerSearchTerm} />
                      <CommandEmpty>Not found</CommandEmpty>
                      <CommandGroup className="max-h-48 overflow-y-auto">
                        {filteredCustomers.map(c => (
                          <CommandItem key={c.id} value={c.id} onSelect={() => { setLeadFormData(p => ({ ...p, customer_id: c.id })); setCustomerSearchOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${leadFormData.customer_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="truncate">
                              <div className="font-medium text-sm">{c.company_name}</div>
                              <div className="text-xs text-gray-500">{c.mobile}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-sm">Name *</Label>
                  <Input value={leadFormData.non_customer_name} onChange={(e) => setLeadFormData(p => ({ ...p, non_customer_name: e.target.value }))} placeholder="Contact name" required />
                </div>
                <div>
                  <Label className="text-sm">Mobile *</Label>
                  <Input value={leadFormData.non_customer_mobile} onChange={(e) => setLeadFormData(p => ({ ...p, non_customer_mobile: e.target.value }))} placeholder="Mobile" required />
                </div>
              </>
            )}

            <div>
              <Label className="text-sm">Source *</Label>
              <Select value={leadFormData.lead_source} onValueChange={(v) => setLeadFormData(p => ({ ...p, lead_source: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Phone Call">Phone Call</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Walk-in">Walk-in</SelectItem>
                  <SelectItem value="Referral">Referral</SelectItem>
                  <SelectItem value="Website">Website</SelectItem>
                  <SelectItem value="Social Media">Social Media</SelectItem>
                  <SelectItem value="Exhibition">Exhibition</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm">Interested Products (Optional)</Label>
              <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between text-left">
                    <span className="text-gray-500">Search products...</span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search product..." value={productSearchTerm} onValueChange={setProductSearchTerm} />
                    <CommandEmpty>No products found</CommandEmpty>
                    <CommandGroup className="max-h-48 overflow-y-auto">
                      {filteredProducts.slice(0, 20).map(p => (
                        <CommandItem key={p.id} value={p.id} onSelect={() => handleAddProduct(p)}>
                          <Check className={`mr-2 h-4 w-4 ${leadFormData.interested_products.find(ip => ip.id === p.id) ? 'opacity-100' : 'opacity-0'}`} />
                          <div className="truncate">
                            <div className="font-medium text-sm">{p.name}</div>
                            <div className="text-xs text-gray-500">SKU: {p.sku}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              {leadFormData.interested_products.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {leadFormData.interested_products.map(p => (
                    <Badge key={p.id} variant="secondary" className="text-xs cursor-pointer hover:bg-red-100" onClick={() => handleRemoveProduct(p.id)}>
                      {p.name} <XCircle className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm">Inquiry *</Label>
              <Textarea value={leadFormData.inquiry_details} onChange={(e) => setLeadFormData(p => ({ ...p, inquiry_details: e.target.value }))} placeholder="Details..." rows={3} required />
            </div>

            <div>
              <Label className="text-sm">Due Date *</Label>
              <Input type="date" value={leadFormData.due_date} onChange={(e) => setLeadFormData(p => ({ ...p, due_date: e.target.value }))} min={new Date().toISOString().split('T')[0]} required />
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateLeadDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto">{loading ? 'Creating...' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesMarketingDashboard;
